import React, { useState } from "react";
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { HeaderTitle } from "../../../../components/HeaderTitle";
import { TypeUsername } from "../../../auth/enum";
import { IOptionTypeUsername } from "../../../auth/interfaces";
import { Switch } from "../../../../components/Switch";
import { Button } from "../../../../components/Button";
import { Input } from "../../../../components/Input";
import { Phone } from "../../../../components/PhoneInput";
import {
  IFormSendInviteCollaborator,
  ISendInviteCollaborator,
} from "../../interfaces";
import {
  Container,
  Content,
  Form,
  TitleForm,
  ContentForm,
  WrapperAccessPermissions,
  ContentSubmitButton,
} from "./styles";
import { UnControlCheckbox as Checkbox } from "../../../../components/Checkbox";
import { useAuth } from "../../../../hooks/useAuth";
import { sendInvite } from "../../../../services/collaborators";
import { useNotification } from "../../../../hooks/useNotification";
import { useNavigate } from "react-router-dom";
import { useTranslate } from "../../../../hooks/useTranslate";
import { formatDateToAPI } from "../../../../utils/format-date";

const NewCollaborator = () => {
  const { successNotification, errorNotification } = useNotification();
  const { propertiesIds, application } = useAuth();
  const { translate, currentLanguage } = useTranslate();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [openAccessPermissions, setOpenAccessPermissions] = useState(false);
  const [signType, setSignType] = useState<TypeUsername>(TypeUsername.email);

  const schema = yup.object({
    [signType]:
      signType === TypeUsername.email
        ? yup
            .string()
            .email(translate("global.email_invalid"))
            .required(translate("global.email_required"))
        : yup.string().required(translate("global.phone_required")),
    name: yup.string().required(translate("global.name_required")),
    date_birth: yup.date().required(translate("global.dob_required")),
    office: yup.string().required(translate("global.office_required")),
  });

  const {
    register,
    control,
    handleSubmit,
    unregister,
    formState: { errors },
  } = useForm<IFormSendInviteCollaborator>({
    resolver: yupResolver(schema),
  });

  const selectSignType = (value: TypeUsername) => {
    unregister(value === TypeUsername.email ? "email" : "phone");
    setSignType(value);
  };

  const options: IOptionTypeUsername[] = [
    { value: TypeUsername.email, label: translate("global.email") },
    { value: TypeUsername.phone, label: translate("global.phone") },
  ];

  const submitForm = async ({
    date_birth,
    name,
    permissions,
    email,
    phone,
    office,
  }: IFormSendInviteCollaborator) => {
    try {
      const languageEmail = {
        PT: "pt-br",
        EN: "en-us",
        ES: "es-es",
      };

      setIsLoading(true);
      const body: ISendInviteCollaborator = {
        name,
        date_birth: formatDateToAPI(date_birth),
        office,
        id_farm: propertiesIds[0],
        language: languageEmail[currentLanguage.language],
        permissions: [
          {
            application,
            permission: permissions,
          },
        ],
      };

      if (email) body.email = email;
      if (phone) body.phone = phone;

      console.log(body);

      await sendInvite({
        application,
        data: body,
      });

      setIsLoading(false);
      successNotification(translate("collaborator.success_message"));
      navigate("/collaborators");
    } catch {
      errorNotification(translate("collaborator.error_message"));
      setIsLoading(false);
    }
  };

  return (
    <Container>
      <HeaderTitle
        title={translate("collaborator.title_new")}
        showIcon
        url="/collaborators"
      />
      <Content>
        <Form onSubmit={handleSubmit(submitForm)}>
          <ContentForm>
            <TitleForm>{translate("collaborator.send_invivation")}</TitleForm>
            <Switch
              currentActive={signType}
              options={options}
              onChange={(value) => {
                selectSignType(value as any);
              }}
            />

            <Input
              {...register("name")}
              error={errors.name?.message}
              placeholder={translate(
                "collaborator.placeholder_name_collaborator"
              )}
            />

            {signType === TypeUsername.email ? (
              <Input
                {...register("email")}
                error={errors.email?.message}
                placeholder={translate("global.placeholder_email")}
                type="email"
              />
            ) : (
              <Controller
                name="phone"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Phone
                    placeholder={translate("global.placeholder_phone")}
                    value={value}
                    onChange={onChange}
                    defaultCountry="BR"
                    error={errors.phone?.message}
                  />
                )}
              />
            )}
            <Input
              {...register("date_birth")}
              type="date"
              name="date_birth"
              error={errors.date_birth?.message}
              placeholder={translate("global.placeholder_date_birth")}
            />
            <Input
              {...register("office")}
              error={errors.office?.message}
              placeholder={translate("global.placeholder_office")}
            />
            <Button
              label={translate("collaborator.label_button_permissions")}
              type="button"
              onClick={() => {
                setOpenAccessPermissions(!openAccessPermissions);
              }}
            />

            {openAccessPermissions && (
              <WrapperAccessPermissions>
                <Checkbox
                  {...register("permissions")}
                  label={translate("collaborator.label_admin")}
                  labelFor="Admin"
                  value="admin"
                  extraInformation={translate("collaborator.describe_admin")}
                />
                <Checkbox
                  {...register("permissions")}
                  label={translate("collaborator.label_property")}
                  labelFor="property"
                  value="property"
                />
                <Checkbox
                  {...register("permissions")}
                  label={translate("collaborator.label_collaborator")}
                  labelFor="collaborator"
                  value="collaborator"
                />
                <Checkbox
                  {...register("permissions")}
                  label={translate("collaborator.label_animals")}
                  labelFor="animals"
                  value="animals"
                />
                <Checkbox
                  {...register("permissions")}
                  label={translate("collaborator.label_tasks")}
                  labelFor="tasks"
                  value="tasks"
                />
              </WrapperAccessPermissions>
            )}
          </ContentForm>

          <ContentSubmitButton>
            <Button
              label={translate("collaborator.send_invivation")}
              type="submit"
              isLoading={isLoading}
            />
          </ContentSubmitButton>
        </Form>
      </Content>
    </Container>
  );
};

export { NewCollaborator };
